import React, { useState, useEffect, useRef } from "react";
import BaseModal from "../../../components/BaseModal";
import { showMainToast } from "utils/showToast";
import { useSession } from "hooks/useSession";

export default function BannerFormModal({ show, onClose, mode, userData, onSuccess }) {
  const [errors, setErrors] = useState({});
  const [previewImage, setPreviewImage] = useState(null);
  const fileInputRef = useRef(null);
  const { userid } = useSession();

  useEffect(() => {
    if (show) {
      setErrors({});
      setPreviewImage(null);
      if (mode === "add") {
        setTimeout(() => clearForm(), 100);
      } else if (mode === "edit" && userData?.photo) {
        setPreviewImage(userData.photo);
      }
    }
  }, [show, mode]);

  const clearForm = () => {
    document.getElementById("banner-form-validation")?.reset();
    setPreviewImage(null);
  };

  const validateForm = () => {
    const bannerName = document.getElementById("banner_name").value.trim();
    const bannerType = document.querySelector('select[name="banner_type"]').value;
    const bannerImage = fileInputRef.current?.files[0];

    let newErrors = {};

    if (!bannerName) {
      newErrors.banner_name = "Banner name is required.";
    } else if (bannerName.length < 3) {
      newErrors.banner_name = "Banner name must be at least 3 characters.";
    }

    if (!bannerType) {
      newErrors.banner_type = "Please select a banner type.";
    }

    if (mode === "add" && !bannerImage) {
      newErrors.banner_image = "Please upload a banner image.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) setPreviewImage(URL.createObjectURL(file));
  };

  const handleSave = async () => {
    if (!validateForm()) {
      showMainToast("Please correct the errors before saving.", "danger");
      return;
    }

    const bannerName = document.getElementById("banner_name").value.trim();
    const bannerType = document.querySelector('select[name="banner_type"]').value;
    const bannerImage = fileInputRef.current?.files[0];

    const formData = new FormData();
    formData.append("action", mode === "edit" ? "UPDATE" : "ADD"); // ✅ action field
    formData.append("banner_name", bannerName);
    formData.append("banner_type", bannerType);
    formData.append("company", userid);
    if (mode === "edit") {
      formData.append("banner_id", userData?.id);               // ✅ required for UPDATE
    }
    if (bannerImage) {
      formData.append("banner_image", bannerImage);
    }

    try {
      const response = await fetch("https://pro.mokshiq.in/banner_management", {
        method: "POST",                                          // ✅ always POST
        headers: {
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
        body: formData,
      });

      const result = await response.json();
      if (result.status) {
        showMainToast(result.data || "Success!", "success");
        setErrors({});
        if (onSuccess) onSuccess();
      } else {
        showMainToast(result.data || "Something went wrong!", "danger");
      }
    } catch (error) {
      console.error("Error:", error);
      showMainToast("Something went wrong!", "danger");
    }
  };
  const handleClose = () => {
    setErrors({});
    setPreviewImage(null);
    onClose();
  };

  return (
    <BaseModal
      show={show}
      onClose={handleClose}
      title={mode === "edit" ? "Edit Banner" : "Add Banner"}
      footerButtons={[
        { label: "Cancel", className: "btn-secondary", dismiss: true },
        {
          label: mode === "edit" ? "Update" : "Save",
          className: "btn-primary",
          onClick: handleSave,
        },
      ]}
    >
      <div className="card-body">
        <form className="row g-3 app-form rounded-control" id="banner-form-validation">

          {/* Banner Name — API: banner_name */}
          <div className="col-md-6">
            <label className="form-label" htmlFor="banner_name">Banner Name</label>
            <input
              className={`form-control ${errors.banner_name ? "is-invalid" : ""}`}
              id="banner_name"
              name="banner_name"
              type="text"
              defaultValue={userData?.name || ""}    // ✅ GET returns "name"
            />
            {errors.banner_name && <div className="text-danger mt-1">{errors.banner_name}</div>}
          </div>

          {/* Banner Type — API: banner_type, GET returns "type" */}
          <div className="col-md-6">
            <label className="form-label">Banner Type</label>
            <select
              className={`form-select w-100 ${errors.banner_type ? "is-invalid" : ""}`}
              name="banner_type"
              defaultValue={userData?.type || ""}    // ✅ GET returns "type"
            >
              <option value="">-- Select --</option>
              <option value="True">Active</option>
              <option value="False">Inactive</option>
            </select>
            {errors.banner_type && <div className="text-danger mt-1">{errors.banner_type}</div>}
          </div>

          {/* Banner Image — API: banner_image, GET returns "photo" */}
          <div className="col-md-12">
            <label className="form-label">
              Banner Image {mode === "edit" && <span className="text-muted">(leave empty to keep existing)</span>}
            </label>
            <input
              className={`form-control ${errors.banner_image ? "is-invalid" : ""}`}
              type="file"
              name="banner_image"
              accept="image/*"
              ref={fileInputRef}
              onChange={handleImageChange}
            />
            {errors.banner_image && <div className="text-danger mt-1">{errors.banner_image}</div>}
            {previewImage && (
              <div className="mt-2">
                <img
                  src={previewImage}
                  alt="Banner Preview"
                  style={{
                    maxHeight: "120px",
                    maxWidth: "100%",
                    borderRadius: "6px",
                    border: "1px solid #dee2e6",
                    objectFit: "contain",
                  }}
                />
              </div>
            )}
          </div>

        </form>
      </div>
    </BaseModal>
  );
}